/**
 * R11 #662 (PRQ-08.3 Model) — Data › Model: the model read for everyone, in Greg's order —
 * Interrogatives → Primitives → Entity types — each type with its fields (inherited from the primitive
 * or added), relationship types in both directions, workflows, checks, the apps that use it and its
 * record count. Read only: there is no control here; the model is changed in Admin › Model editor.
 */
import { useSearchParams } from 'react-router-dom';
import { ErrorState, LoadingRows } from '@/features/governance/ui/primitives';
import { type DataModel, type ModelEntityType, useDataModel } from './api';
import { DataPage } from './DataShell';

function List({ label, items }: { label: string; items: readonly string[] }) {
  if (!items.length) return null;
  return (
    <div>
      <dt className="text-(length:--t-meta) font-semibold text-muted-foreground">{label}</dt>
      <dd className="text-sm text-foreground">{items.join(' · ')}</dd>
    </div>
  );
}

function EntityTypeCard({ t }: { t: ModelEntityType }) {
  return (
    <details id={t.code} className="core-item" data-testid={`model-type-${t.code}`}>
      <summary className="cursor-pointer">
        <span className="font-medium text-foreground">{t.label}</span>{' '}
        <span className="text-(length:--t-meta) text-muted-foreground">
          {t.recordCount} records · {t.apps.length ? `used by ${t.apps.join(', ')}` : 'platform type'}
        </span>
        {t.description ? <span className="block text-sm text-muted-foreground">{t.description}</span> : null}
      </summary>
      <dl className="mt-3 space-y-2">
        <List label="Fields from the primitive" items={t.fields.filter((f) => f.inherited).map((f) => f.label)} />
        <List label="Fields added by this type" items={t.fields.filter((f) => !f.inherited).map((f) => f.label)} />
        <List label="Relationships out" items={t.relationships.filter((r) => r.direction === 'out').map((r) => `${r.label} → ${r.otherTypes.join(', ') || 'any'}`)} />
        <List label="Relationships in" items={t.relationships.filter((r) => r.direction === 'in').map((r) => `${r.label} ← ${r.otherTypes.join(', ') || 'any'}`)} />
        <List label="Workflows" items={t.workflows.map((w) => `${w.label}: ${w.states.join(' → ')}`)} />
        <List label="Checks" items={t.checks.map((c) => c.label)} />
        <List label="Behaviours" items={t.behaviours} />
      </dl>
    </details>
  );
}

/**
 * #770 (Greg: "ask the question, the model narrows") — the questions are the interrogative rows the API
 * returns, never a list in this file; choosing one shows only what answers it. Links, not controls: the
 * model stays read-only (`?question=<code>` keeps the choice in the address).
 */
export function QuestionChips({ model, question, onQuestion }: { model: DataModel; question: string | null; onQuestion?: (code: string | null) => void }) {
  const chip = (code: string | null, label: string) => {
    const on = (question ?? null) === code;
    return (
      <a
        key={code ?? 'all'}
        href={code ? `?question=${encodeURIComponent(code)}` : '?'}
        onClick={onQuestion ? (e) => { e.preventDefault(); onQuestion(code); } : undefined}
        aria-current={on ? 'true' : undefined}
        data-question={code ?? 'all'}
        className={on ? 'rounded-full bg-interactive px-3 py-1 text-(length:--t-meta) font-bold text-interactive-foreground no-underline' : 'rounded-full bg-surface-2 px-3 py-1 text-(length:--t-meta) font-semibold text-muted-foreground no-underline hover:text-foreground'}
      >
        {label}
      </a>
    );
  };
  return (
    <nav aria-label="Ask a question of the model" className="flex flex-wrap items-center gap-1.5" data-testid="model-questions">
      <span className="eyebrow mr-1">Ask</span>
      {chip(null, 'Everything')}
      {model.interrogatives.map((i) => chip(i.code, i.label))}
    </nav>
  );
}

/** The interrogatives a question keeps: all of them, or the one asked (an unknown question keeps all). */
export function narrow(model: DataModel, question: string | null): DataModel['interrogatives'] {
  const hit = model.interrogatives.filter((i) => i.code === question);
  return hit.length ? hit : model.interrogatives;
}

export function ModelView({ model, question = null, onQuestion }: { model: DataModel; question?: string | null; onQuestion?: (code: string | null) => void }) {
  return (
    <div className="space-y-6" data-testid="data-model">
      <p className="text-sm text-muted-foreground" aria-hidden>
        Interrogatives → Primitives → Entity types
      </p>
      <QuestionChips model={model} question={question} onQuestion={onQuestion} />
      {narrow(model, question).map((i) => (
        <section key={i.code} aria-label={`Interrogative ${i.label}`} className="grid gap-4 border-t border-border pt-4 lg:grid-cols-[12rem_minmax(0,1fr)]">
          <header>
            <p className="eyebrow">Interrogative</p>
            <h2 className="font-display text-lg font-semibold text-foreground">{i.label}</h2>
            {i.description ? <p className="text-sm text-muted-foreground">{i.description}</p> : null}
          </header>
          <div className="space-y-4">
            {i.primitives.map((p) => (
              <div key={p.code} className="grid gap-3 lg:grid-cols-[12rem_minmax(0,1fr)]">
                <div>
                  <p className="eyebrow">Primitive</p>
                  <h3 className="font-medium text-foreground">{p.label}</h3>
                  {p.behaviours.length ? <p className="text-(length:--t-meta) text-muted-foreground">Behaviours: {p.behaviours.join(', ')}</p> : null}
                </div>
                <div className="space-y-2">
                  <p className="eyebrow">Entity types</p>
                  {p.entityTypes.length ? p.entityTypes.map((t) => <EntityTypeCard key={t.code} t={t} />) : <p className="text-sm text-muted-foreground">No entity type yet.</p>}
                </div>
              </div>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

export default function ModelPage() {
  const q = useDataModel();
  const [params, setParams] = useSearchParams();
  const question = params.get('question');
  const ask = (code: string | null) => setParams(code ? { question: code } : {}, { replace: true });
  return (
    <DataPage title="Model" description="What the platform records and how it fits together. Everyone can read it; administrators change it in Admin.">
      {q.isLoading ? <LoadingRows /> : q.error ? <ErrorState error={q.error} /> : q.data ? <ModelView model={q.data} question={question} onQuestion={ask} /> : null}
    </DataPage>
  );
}
